// Offline Storage Service using IndexedDB for SRS cards
const DB_NAME = 'thai-journal-offline';
const DB_VERSION = 1;
const STORES = {
  SRS_CARDS: 'srs_cards',
  PENDING_REVIEWS: 'pending_reviews',
  STATS: 'stats'
};

class OfflineStorage {
  constructor() {
    this.db = null;
    this.isReady = false;
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[OfflineStorage] Error opening database');
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isReady = true;
        console.log('[OfflineStorage] Database ready');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // SRS Cards store
        if (!db.objectStoreNames.contains(STORES.SRS_CARDS)) {
          const cardsStore = db.createObjectStore(STORES.SRS_CARDS, { keyPath: 'id' });
          cardsStore.createIndex('category', 'category', { unique: false });
          cardsStore.createIndex('next_review', 'next_review', { unique: false });
        }

        // Pending reviews store (for offline sync)
        if (!db.objectStoreNames.contains(STORES.PENDING_REVIEWS)) {
          db.createObjectStore(STORES.PENDING_REVIEWS, { keyPath: 'id', autoIncrement: true });
        }

        // Stats store
        if (!db.objectStoreNames.contains(STORES.STATS)) {
          db.createObjectStore(STORES.STATS, { keyPath: 'key' });
        }

        console.log('[OfflineStorage] Database schema created');
      };
    });
  }

  // Save all SRS cards for offline use
  async saveCards(cards) {
    if (!this.isReady) await this.init();

    const tx = this.db.transaction(STORES.SRS_CARDS, 'readwrite');
    const store = tx.objectStore(STORES.SRS_CARDS);

    for (const card of cards) {
      store.put(card);
    }

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => {
        console.log(`[OfflineStorage] Saved ${cards.length} cards`);
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  // Get all cards due for review
  async getDueCards(limit = 20) {
    if (!this.isReady) await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.SRS_CARDS, 'readonly');
      const store = tx.objectStore(STORES.SRS_CARDS);
      const now = new Date().toISOString();
      const cards = [];

      const request = store.openCursor();
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor && cards.length < limit) {
          const card = cursor.value;
          // Check if card is due (next_review <= now or never reviewed)
          if (!card.next_review || card.next_review <= now) {
            cards.push(card);
          }
          cursor.continue();
        } else {
          resolve(cards);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Get all cards
  async getAllCards() {
    if (!this.isReady) await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.SRS_CARDS, 'readonly');
      const store = tx.objectStore(STORES.SRS_CARDS);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Update a single card after review
  async updateCard(card) {
    if (!this.isReady) await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.SRS_CARDS, 'readwrite');
      const store = tx.objectStore(STORES.SRS_CARDS);
      const request = store.put(card);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Save a pending review for later sync
  async savePendingReview(review) {
    if (!this.isReady) await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.PENDING_REVIEWS, 'readwrite');
      const store = tx.objectStore(STORES.PENDING_REVIEWS);
      const request = store.add({
        ...review,
        timestamp: new Date().toISOString()
      });

      request.onsuccess = () => {
        console.log('[OfflineStorage] Saved pending review');
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Get all pending reviews
  async getPendingReviews() {
    if (!this.isReady) await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.PENDING_REVIEWS, 'readonly');
      const store = tx.objectStore(STORES.PENDING_REVIEWS);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Clear pending reviews after sync
  async clearPendingReviews() {
    if (!this.isReady) await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.PENDING_REVIEWS, 'readwrite');
      const store = tx.objectStore(STORES.PENDING_REVIEWS);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('[OfflineStorage] Cleared pending reviews');
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Save stats
  async saveStats(stats) {
    if (!this.isReady) await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.STATS, 'readwrite');
      const store = tx.objectStore(STORES.STATS);
      const request = store.put({ key: 'srs_stats', ...stats });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Get stats
  async getStats() {
    if (!this.isReady) await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.STATS, 'readonly');
      const store = tx.objectStore(STORES.STATS);
      const request = store.get('srs_stats');

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  // Calculate offline stats
  async calculateOfflineStats() {
    const cards = await this.getAllCards();
    const now = new Date().toISOString();

    const total = cards.length;
    const learned = cards.filter(c => c.interval > 21).length;
    const newCards = cards.filter(c => !c.last_reviewed).length;
    const learning = total - learned - newCards;
    const dueToday = cards.filter(c => !c.next_review || c.next_review <= now).length;

    const easeSum = cards.reduce((sum, c) => sum + (c.ease_factor || 2.5), 0);
    const avgEase = total > 0 ? easeSum / total : 2.5;

    const totalReviews = cards.reduce((sum, c) => sum + (c.total_reviews || 0), 0);
    const correctReviews = cards.reduce((sum, c) => sum + (c.correct_reviews || 0), 0);
    const accuracy = totalReviews > 0 ? (correctReviews / totalReviews) * 100 : 0;

    return {
      total_cards: total,
      cards_due_today: dueToday,
      cards_learned: learned,
      cards_learning: learning,
      cards_new: newCards,
      average_ease: avgEase,
      accuracy_rate: accuracy,
      is_offline: true
    };
  }
}

// Singleton instance
const offlineStorage = new OfflineStorage();

// SM-2 Algorithm implementation for offline use
export function calculateSM2Offline(quality, easeFactor, interval, repetitions) {
  let newEF = easeFactor;
  let newInterval = interval;
  let newReps = repetitions;

  if (quality < 3) {
    // Failed - reset
    newReps = 0;
    newInterval = 1;
  } else {
    // Success
    newReps = repetitions + 1;
    if (newReps === 1) {
      newInterval = 1;
    } else if (newReps === 2) {
      newInterval = 6;
    } else {
      newInterval = Math.round(interval * easeFactor);
    }
  }

  // Update ease factor
  newEF = easeFactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02));
  newEF = Math.max(1.3, newEF);

  return { newEaseFactor: newEF, newInterval, newRepetitions: newReps };
}

export default offlineStorage;
